import React, { useEffect, useMemo } from "react";
import styles from "./UserInfoStep.module.scss";
import SignUpFormWrapper from "./wrapper/SignUpFormWrapper";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../stores/store";
import { useDispatch, useSelector } from "react-redux";
import { setActiveStep, setSignUpId, setUserInfo, userInfoSelector } from "../../stores/slices/sign-up-slice";
import { ApplicationRoutes } from "../../routes";
import Input from "../../common/components/new/ui/Input";
import { KeyIcon, LetterIcon, ProfileIcon } from "../../assets/icons";
import { useForm } from "react-hook-form";
import { useInitiateSignupMutation } from "../../service/customer-service";
import { toast } from "react-toastify";
import CustomerSignUpFormWrapper from "./wrapper/CustomerSignUpFormWrapper";

type Props = {};

const UserInfoStep: React.FC<Props> = (props: Props) => {
	const [initiateSignup, { isLoading }] = useInitiateSignupMutation();

	const dispatch = useDispatch<AppDispatch>();

	const navigate = useNavigate();

	const userInfo = useSelector(userInfoSelector);

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isValid, isSubmitted },
	} = useForm<{
		firstName: string;
		lastName: string;
		email: string;
		password: string;
		confirmPassword: string;
	}>({
		defaultValues: {
			...userInfo,
			password: "",
			confirmPassword: "",
		},
		reValidateMode: "onSubmit",
		mode: "onBlur",
	});

	const registeredInputs = useMemo(() => {
		return {
			firstName: register("firstName", { required: "First name is required" }),
			lastName: register("lastName", { required: "Last name is required" }),
			email: register("email", {
				required: "Email is required",
				pattern: {
					value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
					message: "Invalid email address",
				},
			}),
			password: register("password", {
				required: "Password is required",
				validate: value => validatePassword(value),
			}),
			confirmPassword: register("confirmPassword", {
				required: "Confirm password is required",
				validate: value => value === watch("password") || "Passwords do not match",
			}),
		};
	}, [register, watch]);

	const validatePassword = (value: string) => {
		// Check if password is at least 10 characters long
		if (value.length === 0) {
			return "Password is required";
		}

		// Check if password is at least 10 characters long
		if (value.length < 10) {
			return "Password must be at least 10 characters long";
		}

		// Check if password contains at least one uppercase letter
		if (!/[A-Z]/.test(value)) {
			return "Password must contain at least one uppercase letter";
		}

		// Check if password contains at least one lowercase letter
		if (!/[a-z]/.test(value)) {
			return "Password must contain at least one lowercase letter";
		}

		// Check if password contains at least one number
		if (!/\d/.test(value)) {
			return "Password must contain at least one number";
		}

		// Check if password contains at least one special character
		if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)) {
			return "Password must contain at least one special character";
		}

		return true;
	};

	const onSubmit = async () => {
		try {
			const result = await initiateSignup({
				email: watch("email"),
				firstName: watch("firstName"),
				lastName: watch("lastName"),
				password: watch("password"),
			}).unwrap();

			// dispatch(
			// 	setUserInfo({
			// 		firstName: watch("firstName"),
			// 		lastName: watch("lastName"),
			// 		email: watch("email"),
			// 	})
			// );

			dispatch(setSignUpId(result.customerSignupId));

			navigate(`/${ApplicationRoutes.NEW_CUSTOMER_SIGNUP_OTP}`);
		} catch (error: any) {
			toast.error(error.data?.title || "Failed to sign up. Please try again.");
		}
	};

	const onBack = () => {
		navigate(ApplicationRoutes.LANDING_PAGE);
	};

	useEffect(() => {
		dispatch(setActiveStep(1));
	}, [dispatch]);

	return (
		<CustomerSignUpFormWrapper
			title="Let's Fire you up!"
			subtitle="Please input your personal information"
			onBack={onBack}
			onNext={handleSubmit(onSubmit)}
			nextButtonDisabled={isSubmitted && !isValid}
			isLoading={isLoading}
		>
			<div className={styles.container}>
				<div className={styles.inputGroup}>
					<Input
						ref={registeredInputs.firstName.ref}
						label="First Name"
						placeholder="Name 1"
						error={errors.firstName?.message}
						inputProps={registeredInputs.firstName}
					>
						<ProfileIcon />
					</Input>
					<Input
						ref={registeredInputs.lastName.ref}
						label="Last Name"
						placeholder="Name 2"
						error={errors.lastName?.message}
						inputProps={registeredInputs.lastName}
					>
						<ProfileIcon />
					</Input>
				</div>

				<Input
					ref={registeredInputs.email.ref}
					label="Email Address"
					placeholder="name@sample.com"
					error={errors.email?.message}
					inputProps={registeredInputs.email}
				>
					<LetterIcon />
				</Input>

				<Input
					ref={registeredInputs.password.ref}
					label="Password"
					placeholder="Enter your password"
					error={errors.password?.message}
					isPasswordInput
					inputProps={registeredInputs.password}
				>
					<KeyIcon />
				</Input>

				<Input
					ref={registeredInputs.confirmPassword.ref}
					label="Confirm Password"
					placeholder="Confirm your password"
					error={errors.confirmPassword?.message}
					isPasswordInput
					inputProps={registeredInputs.confirmPassword}
				>
					<KeyIcon />
				</Input>
			</div>
		</CustomerSignUpFormWrapper>
	);
};

export default UserInfoStep;
